import { useState } from 'react';

const DEFAULT_TASKS = [
  'Ausschreibung prüfen & bewerten',
  'Referenzen auswählen & aufbereiten',
  'Angebot / Bewerbung schreiben',
  'Unterlagen zusammenstellen',
  'Einreichen & Frist überwachen',
];

const BUERO_OPTIONS = ['PA+', 'L&L', 'PWK', 'PA+/L&L', 'PA+/PWK'];
const OBJEKTART_OPTIONS = ['Schule', 'Wohngebäude', 'Verwaltung', 'Kultur', 'Sport', 'Gesundheit', 'Infrastruktur', 'Sonstiges'];
const MASSNAHME_OPTIONS = ['Neubau', 'Sanierung', 'Umbau', 'Erweiterung', 'Neubau/Sanierung'];

const BUERO_SHORT = { 'PA+': 'pap', 'L&L': 'lel', 'PWK': 'pwk', 'PA+/L&L': 'pap-lel', 'PA+/PWK': 'pap-pwk' };
const OBJEKT_SHORT = {
  'Schule': 'schule', 'Wohngebäude': 'wohnen', 'Verwaltung': 'verwaltung',
  'Kultur': 'kultur', 'Sport': 'sport', 'Gesundheit': 'gesundheit',
  'Infrastruktur': 'infra', 'Sonstiges': 'sonstiges'
};
const MASSNAHME_SHORT = {
  'Neubau': 'nb', 'Sanierung': 'san', 'Umbau': 'umbau',
  'Erweiterung': 'erw', 'Neubau/Sanierung': 'nb-san'
};

function getDatePart(deadline) {
  if (deadline) {
    const d = new Date(deadline);
    if (!isNaN(d)) {
      return String(d.getFullYear()).slice(2) + String(d.getMonth()+1).padStart(2,'0') + String(d.getDate()).padStart(2,'0');
    }
  }
  const now = new Date();
  return String(now.getFullYear()).slice(2) + String(now.getMonth()+1).padStart(2,'0') + String(now.getDate()).padStart(2,'0');
}

function buildIdentifier(deadline, buero, objektart, massnahme) {
  const ts = Date.now().toString().slice(-5); // 5-stelliger Timestamp-Suffix
  return `${getDatePart(deadline)}-${BUERO_SHORT[buero]||'b'}-${OBJEKT_SHORT[objektart]||'o'}-${MASSNAHME_SHORT[massnahme]||'m'}-${ts}`;
}

function buildName(deadline, buero, objektart, massnahme) {
  return `${getDatePart(deadline)}_${buero}_${objektart}_${massnahme}`;
}

const SelectField = ({ label, value, onChange, options }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, background: 'white', cursor: 'pointer' }}>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export default function ExportModal({ selected, results, settings, onExport, onClose }) {
  const notices = results.filter(r => selected.has(r.id));
  const firstNotice = notices[0];

  const [buero, setBuero] = useState('PA+');
  const [objektart, setObjektart] = useState('Schule');
  const [massnahme, setMassnahme] = useState('Neubau');
  const [tasks, setTasks] = useState([...DEFAULT_TASKS]);
  const [createFolder, setCreateFolder] = useState(true);
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);

  const deadline = firstNotice?.deadline || '';
  const projectName = buildName(deadline, buero, objektart, massnahme);

  const addTask = () => setTasks(t => [...t, '']);
  const updateTask = (i, v) => setTasks(t => t.map((x, j) => j === i ? v : x));
  const removeTask = (i) => setTasks(t => t.filter((_, j) => j !== i));

  const run = async () => {
    setRunning(true);
    setLog([]);
    setStatus({ type: 'info', text: 'Export läuft…' });

    const identifier = buildIdentifier(deadline, buero, objektart, massnahme);

    const { successes, errors } = await onExport({
      projectId: identifier,
      projectName,
      buero,
      objektart,
      massnahme,
      tasks: tasks.filter(Boolean),
      createFolder
    });

    setLog([
      ...successes.map(s => ({ type: 'ok', text: `✅ ${(s.notice?.title || '').slice(0, 50)}… → Projekt angelegt` })),
      ...errors.map(e => ({ type: 'err', text: `❌ ${(e.notice?.title || '').slice(0, 50)}… → ${e.error}` })),
    ]);
    setStatus({
      type: errors.length === 0 ? 'success' : 'warn',
      text: `${successes.length} erfolgreich${errors.length > 0 ? `, ${errors.length} fehlgeschlagen` : ''}`,
    });
    setRunning(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📋 OpenProject Export</h2>
          <button className="btn btn-sm" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 13, color: '#666' }}>
            <strong>{notices.length}</strong> Ausschreibung{notices.length !== 1 ? 'en' : ''} werden als Unterprojekt{notices.length !== 1 ? 'e' : ''} in Akquise angelegt.
          </div>

          {/* Projektbezeichnung */}
          <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 14, border: '1px solid #eee' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 10 }}>Projektbezeichnung</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
              <SelectField label="Büro" value={buero} onChange={setBuero} options={BUERO_OPTIONS} />
              <SelectField label="Objektart" value={objektart} onChange={setObjektart} options={OBJEKTART_OPTIONS} />
              <SelectField label="Maßnahme" value={massnahme} onChange={setMassnahme} options={MASSNAHME_OPTIONS} />
            </div>
            <div style={{ background: 'white', borderRadius: 6, padding: '8px 12px', border: '1px solid #ddd' }}>
              <div style={{ fontSize: 11, color: '#aaa', marginBottom: 2 }}>Projektname in OpenProject</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a5c3a', fontFamily: 'monospace' }}>{projectName}</div>
            </div>
            {deadline && (
              <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
                Abgabefrist: <strong>{deadline}</strong>
              </div>
            )}
          </div>

          {/* Aufgaben */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>Aufgaben</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 8 }}>
              {tasks.map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: 6 }}>
                  <input type="text" value={t} onChange={e => updateTask(i, e.target.value)} style={{ flex: 1 }} />
                  <button className="btn btn-sm" onClick={() => removeTask(i)} style={{ padding: '4px 8px' }}>✕</button>
                </div>
              ))}
            </div>
            <button className="btn btn-sm" onClick={addTask}>+ Aufgabe hinzufügen</button>
          </div>

          {/* SharePoint */}
          <div style={{ borderTop: '1px solid #eee', paddingTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <input type="checkbox" id="cfolder" checked={createFolder} onChange={e => setCreateFolder(e.target.checked)} />
              <label htmlFor="cfolder" style={{ fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                📁 SharePoint-Ordner anlegen
              </label>
            </div>
            {createFolder && (
              <div style={{ fontSize: 11, color: '#999', paddingLeft: 24 }}>
                Ordner: <code style={{ background: '#f0f0f0', padding: '1px 5px', borderRadius: 3 }}>{projectName}</code>
              </div>
            )}
          </div>

          {status && <div className={`status-banner status-${status.type}`}>{status.text}</div>}

          {log.length > 0 && (
            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 12, fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {log.map((l, i) => (
                <div key={i} style={{ color: l.type === 'ok' ? '#1a5c3a' : '#c0392b' }}>{l.text}</div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Schließen</button>
          <button className="btn btn-primary" onClick={run} disabled={running}>
            {running ? '⏳ Läuft…' : '🚀 In OpenProject erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
}
